// exportUtils.js - FIXED VERSION
import { notification } from 'antd';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { autoTable } from 'jspdf-autotable';

const exportUtils = {
  /**
   * Export data to CSV file
   * @param {Array} data - Array of objects to export
   * @param {Array} headers - Array of column names 
   * @param {string} filename - Name of the file
   */
  exportToCSV: (data, headers, filename) => {
    try {
      // Create CSV content
      let csvContent = "";
      
      // Add headers
      if (headers && headers.length > 0) {
        csvContent += headers.join(',') + '\r\n';
      }
      
      // Add rows
      data.forEach(item => {
        const values = headers.map(header => {
          let value = item[header] !== undefined ? item[header] : '';
          
          // Handle values with commas or quotes
          if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
            // Escape quotes and wrap in quotes
            value = '"' + value.replace(/"/g, '""') + '"';
          }
          
          return value;
        });
        
        csvContent += values.join(',') + '\r\n';
      });
      
      // Create download link
      const encodedUri = encodeURI('data:text/csv;charset=utf-8,' + csvContent);
      const link = document.createElement('a');
      link.setAttribute('href', encodedUri);
      link.setAttribute('download', `${filename}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      return true;
    } catch (error) {
      console.error('Error exporting to CSV:', error);
      notification.error({
        message: 'Export Failed',
        description: 'Failed to export data to CSV'
      });
      return false;
    }
  },
  
  /**
   * Export data to Excel file with proper currency formatting
   * @param {Array} data - Array of objects to export
   * @param {Object} options - Configuration options
   */
  exportToExcel: (data, options) => {
    try {
      const { 
        columns, 
        filename, 
        sheetName = 'Sheet1', 
        fitToPage = false, 
        orientation = 'portrait',
        nestedItems = false,
        groupedView = false
      } = options;
      
      // Convert data to worksheet format
      const wsData = [];
      
      // Add headers
      const headers = columns.map(col => col.title);
      wsData.push(headers);
      
      // Track rows that need special formatting
      const specialFormatting = [];
      
      // Process data based on the view type
      if (nestedItems) {
        // For order items view - create special formatting with indentation
        let rowIndex = 1; // Start after headers
        
        data.forEach(item => {
          if (item.isOrderHeader) {
            // Add order header row
            const row = columns.map(col => {
              let value;
              
              if (col.render) {
                value = col.render(item[col.dataIndex], item);
              } else {
                value = item[col.dataIndex];
              }
              
              return value !== undefined ? value : '';
            });
            
            wsData.push(row);
            
            // Mark this row as an order header for styling
            specialFormatting.push({
              row: rowIndex,
              type: 'header'
            });
            
            rowIndex++;
          } else {
            // Add item row with indentation
            const row = columns.map((col, colIndex) => {
              let value;
              
              if (col.render) {
                value = col.render(item[col.dataIndex], item);
              } else {
                value = item[col.dataIndex];
              }
              
              // Add indentation to the first column for items
              if (colIndex === 0 && value !== undefined && value !== '') {
                value = `    • ${value}`;
              }
              
              return value !== undefined ? value : '';
            });
            
            wsData.push(row);
            
            // Mark this row as an item for styling
            specialFormatting.push({
              row: rowIndex,
              type: 'item'
            });
            
            rowIndex++;
          }
        });
      } else if (groupedView) {
        // For grouped property view
        let rowIndex = 1; // Start after headers
        
        data.forEach(item => {
          if (item.isPropertyGroup) {
            // Add property group header
            const row = columns.map(col => {
              let value;
              
              if (col.render) {
                value = col.render(item[col.dataIndex], item);
              } else {
                value = item[col.dataIndex];
              }
              
              return value !== undefined ? value : '';
            });
            
            wsData.push(row);
            
            // Mark this row as a property group header for styling
            specialFormatting.push({
              row: rowIndex,
              type: 'propertyHeader'
            });
            
            rowIndex++;
          } else {
            // Add order row under the property
            const row = columns.map((col, colIndex) => {
              let value;
              
              if (col.render) {
                value = col.render(item[col.dataIndex], item);
              } else {
                value = item[col.dataIndex];
              }
              
              // Add indentation to first column for orders in a property
              if (colIndex === 0 && value !== undefined && value !== '') {
                value = `  ${value}`;
              }
              
              return value !== undefined ? value : '';
            });
            
            wsData.push(row);
            
            // Mark as a property item
            specialFormatting.push({
              row: rowIndex,
              type: 'propertyItem'
            });
            
            rowIndex++;
          }
        });
      } else {
        // Standard flat view - just add rows
        data.forEach(item => {
          const row = columns.map(col => {
            let value;
            
            if (col.render) {
              value = col.render(item[col.dataIndex], item);
            } else {
              value = item[col.dataIndex];
            }
            
            return value !== undefined ? value : '';
          });
          
          wsData.push(row);
        });
      }
      
      // Create worksheet
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      
      // Set column widths
      const colWidths = columns.map((col) => ({ wpx: (col.width || 10) * 10 }));
      ws['!cols'] = colWidths;
      
      // Apply special formatting
      for (let r = 1; r < wsData.length; r++) {
        // Find if this row has special formatting
        const rowFormat = specialFormatting.find(f => f.row === r);
        
        for (let c = 0; c < columns.length; c++) {
          const cellRef = XLSX.utils.encode_cell({ r, c });
          const cellValue = ws[cellRef]?.v;
          
          // Apply row-based styling
          if (rowFormat) {
            // Create cell if it doesn't exist
            if (!ws[cellRef]) {
              ws[cellRef] = { v: '', t: 's' };
            }
            
            // Add cell styling based on row type
            if (rowFormat.type === 'header' || rowFormat.type === 'propertyHeader') {
              ws[cellRef].s = {
                fill: { fgColor: { rgb: "EEEEEE" } },
                font: { bold: true }
              };
            } else if (rowFormat.type === 'item' || rowFormat.type === 'propertyItem') {
              ws[cellRef].s = {
                fill: { fgColor: { rgb: "F9F9F9" } }
              };
            }
          }
          
          // Handle currency formatting
          if (typeof cellValue === 'string' && cellValue.includes('£')) {
            const numValue = parseFloat(cellValue.replace('£', '').replace(/,/g, ''));
            if (!isNaN(numValue)) {
              ws[cellRef] = { 
                v: numValue,
                t: 'n',
                z: '"£"#,##0.00;[Red]-"£"#,##0.00',
                s: ws[cellRef]?.s || {}
              };
            }
          }
        }
      }
      
      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
      
      // Set print settings
      if (fitToPage) {
        ws['!printSetup'] = { 
          fitToHeight: 1, 
          fitToWidth: 1,
          orientation: orientation === 'landscape' ? 1 : 0
        };
      }
      
      // Generate Excel file
      XLSX.writeFile(wb, `${filename}.xlsx`);
      
      return true;
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      notification.error({
        message: 'Export Failed',
        description: 'Failed to export data to Excel: ' + error.message
      });
      return false;
    }
  },
  
  /**
   * Export data to PDF file with invoice-like formatting
   * @param {Array} data - Array of objects to export
   * @param {Object} options - Configuration options
   */
  exportToPDF: (data, options) => {
    try {
      const { 
        columns, 
        filename, 
        title = 'Report', 
        summary = null, 
        orientation = 'portrait',
        fitToPage = true,
        fontSize = 10,
        nestedItems = false,
        groupedView = false,
        dateRange = '',
        showPricesWithoutVAT = false
      } = options;
      
      // Create PDF document
      const doc = new jsPDF({
        orientation: orientation,
        unit: 'mm',
        format: 'a4'
      });
      
      // Add company header (invoice-like)
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      
      // Header background
      doc.setFillColor(240, 240, 240);
      doc.rect(0, 0, pageWidth, 40, 'F');
      
      // Company name/logo area
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(50, 50, 50);
      doc.text('SALES REPORT', 14, 20);
      
      // Date and time
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      const today = new Date().toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit', 
        year: 'numeric'
      });
      const time = new Date().toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit'
      });
      doc.text(`Generated: ${today} at ${time}`, pageWidth - 60, 15);
      
      // Report title and period
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(50, 50, 50);
      doc.text(title, 14, 32);
      
      if (dateRange) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text(`Period: ${dateRange}`, 14, 38);
      }
      
      // Reset colors for table
      doc.setTextColor(0, 0, 0);
      
      // Prepare table data based on view type
      let processedRows = [];
      let tableColumns = columns.map(col => ({
        header: col.title,
        dataKey: col.dataIndex || col.key,
        width: col.width || 20
      }));
      
      if (nestedItems) {
        // For order items view with proper grouping
        data.forEach(item => {
          if (item.isOrderHeader) {
            // Add order header row
            const row = {};
            columns.forEach(col => {
              const key = col.dataIndex || col.key;
              let value = '';
              
              if (col.renderText) {
                value = col.renderText(item[key], item);
              } else if (col.render) {
                value = col.render(item[key], item);
              } else {
                value = item[key];
              }
              
              row[key] = value !== undefined ? value : '';
            });
            
            processedRows.push({
              ...row,
              isHeader: true
            });
          } else {
            // Add item row with indentation
            const row = {};
            columns.forEach((col, colIndex) => {
              const key = col.dataIndex || col.key;
              let value = '';
              
              if (col.renderText) {
                value = col.renderText(item[key], item);
              } else if (col.render) {
                value = col.render(item[key], item);
              } else {
                value = item[key];
              }
              
              // Add indentation to first column for items
              if (colIndex === 0 && value) {
                value = `    • ${value}`;
              }
              
              row[key] = value !== undefined ? value : '';
            });
            
            processedRows.push({
              ...row,
              isItem: true
            });
          }
        });
      } else if (groupedView) {
        // For grouped property view
        data.forEach(item => {
          if (item.isPropertyGroup) {
            // Add property group header
            const row = {};
            columns.forEach(col => {
              const key = col.dataIndex || col.key;
              let value = '';
              
              if (col.renderText) {
                value = col.renderText(item[key], item);
              } else if (col.render) {
                value = col.render(item[key], item);
              } else {
                value = item[key];
              }
              
              row[key] = value !== undefined ? value : '';
            });
            
            processedRows.push({
              ...row,
              isHeader: true
            });
          } else {
            // Add order row under property group
            const row = {};
            columns.forEach((col, colIndex) => {
              const key = col.dataIndex || col.key;
              let value = '';
              
              if (col.renderText) {
                value = col.renderText(item[key], item);
              } else if (col.render) {
                value = col.render(item[key], item);
              } else {
                value = item[key];
              }
              
              // Add indentation to first column
              if (colIndex === 0 && value) {
                value = `  ${value}`;
              }
              
              row[key] = value !== undefined ? value : '';
            });
            
            processedRows.push({
              ...row,
              isItem: true
            });
          }
        });
      } else {
        // Standard flat view
        processedRows = data.map(item => {
          const row = {};
          columns.forEach(col => {
            const key = col.dataIndex || col.key;
            let value = '';
            
            if (col.renderText) {
              value = col.renderText(item[key], item);
            } else if (col.render) {
              value = col.render(item[key], item);
            } else {
              value = item[key];
            }
            
            row[key] = value !== undefined ? value : '';
          });
          
          return row;
        });
      }
      
      // Column widths use the document's millimetres, as required by autoTable.
      const totalWidth = columns.reduce((sum, col) => sum + (col.width || 1), 0);
      const colWidths = columns.map(col => ((col.width || 1) / totalWidth) * (pageWidth - 28));
      
      // Add table with invoice-like styling
      autoTable(doc, {
        columns: tableColumns,
        body: processedRows,
        startY: 50,
        margin: { left: 14, right: 14 },
        theme: 'grid',
        styles: {
          fontSize: fontSize,
          cellPadding: 4,
          overflow: 'linebreak',
          lineWidth: 0.1,
          lineColor: [200, 200, 200]
        },
        headStyles: {
          fillColor: [50, 50, 50],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: fontSize + 1
        },
        columnStyles: columns.reduce((styles, col, index) => {
          const key = col.dataIndex || col.key;
          styles[key] = { 
            cellWidth: colWidths[index],
            halign: col.align || 'left'
          };
          return styles;
        }, {}),
        didParseCell: function(data) {
          const rowInfo = processedRows[data.row.index];
          
          // Style headers (property groups or order headers)
          if (rowInfo?.isHeader) {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fillColor = [230, 230, 230];
            data.cell.styles.textColor = [50, 50, 50];
          }
          
          // Style items (order items or property items)
          if (rowInfo?.isItem) {
            data.cell.styles.fillColor = [248, 248, 248];
            
            // Add indentation styling
            if (data.column.index === 0 && data.cell.text[0] && 
                (data.cell.text[0].startsWith('  ') || data.cell.text[0].startsWith('    •'))) {
              data.cell.styles.cellPadding = {
                top: 3, 
                right: 3, 
                bottom: 3, 
                left: data.cell.text[0].startsWith('    •') ? 15 : 10
              };
            }
          }
          
          // Format currency cells
          if (typeof data.cell.text[0] === 'string' && data.cell.text[0].includes('£')) {
            data.cell.styles.halign = 'right';
            data.cell.styles.fontStyle = 'bold';
          }
        },
        // Add footer with summary if provided
        foot: summary ? [summary] : null,
        footStyles: {
          fontStyle: 'bold',
          fillColor: [50, 50, 50],
          textColor: [255, 255, 255]
        },
        didDrawPage: (data) => {
          // Add page number and footer
          doc.setFontSize(8);
          doc.setTextColor(100, 100, 100);
          doc.text(
            `Page ${doc.internal.getNumberOfPages()}`,
            14,
            pageHeight - 10
          );
          
          // Add generation info
          doc.text(
            `Generated on ${today} at ${time}`,
            pageWidth - 60,
            pageHeight - 10
          );
          
          // Add border line at bottom
          doc.setDrawColor(200, 200, 200);
          doc.line(14, pageHeight - 15, pageWidth - 14, pageHeight - 15);
        }
      });
      
      // Save the PDF
      doc.save(`${filename}.pdf`);
      
      return true;
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      notification.error({
        message: 'Export Failed',
        description: 'Failed to export data to PDF: ' + error.message
      });
      return false;
    }
  },
  
  /**
   * Print a specific element
   * @param {HTMLElement} element - Element to print
   * @param {string} title - Document title
   */
  printElement: (element, title) => {
    try {
      // Create a new window
      const printWindow = window.open('', '_blank', 'height=600,width=800');
      
      // Set document content
      printWindow.document.write(`
        <html>
          <head>
            <title>${title}</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                margin: 20px;
                line-height: 1.4;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                margin-bottom: 20px;
              }
              th, td {
                border: 1px solid #ddd;
                padding: 8px;
                text-align: left;
                font-size: 12px;
              }
              th {
                background-color: #f2f2f2;
                font-weight: bold;
              }
              .ant-card {
                border: 1px solid #f0f0f0;
                border-radius: 4px;
                margin-bottom: 16px;
                padding: 16px;
              }
              .ant-statistic-title {
                font-size: 14px;
                color: #595959;
              }
              .ant-statistic-content {
                font-size: 20px;
                font-weight: bold;
              }
              h1, h2, h3 {
                color: #001529;
              }
              .print-header {
                text-align: center;
                margin-bottom: 30px;
                padding-bottom: 20px;
                border-bottom: 2px solid #001529;
              }
              .print-date {
                text-align: right;
                font-size: 12px;
                color: #666;
                margin-bottom: 20px;
              }
              @media print {
                button, .ant-dropdown, .ant-btn, .ant-pagination {
                  display: none !important;
                }
                body {
                  margin: 0;
                }
                .print-break {
                  page-break-before: always;
                }
              }
            </style>
          </head>
          <body>
            <div class="print-header">
              <h1>${title}</h1>
            </div>
            <div class="print-date">
              Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}
            </div>
            ${element.innerHTML}
            <script>
              setTimeout(function() {
                window.print();
                window.close();
              }, 1000);
            </script>
          </body>
        </html>
      `);
      
      printWindow.document.close();
      
      return true;
    } catch (error) {
      console.error('Error printing element:', error);
      notification.error({
        message: 'Print Failed',
        description: 'Failed to print report: ' + error.message
      });
      return false;
    }
  }
};

export default exportUtils;
